import moment from "moment";
import {
    DataResponse,
    EntityResponse,
    MapBoxTokenResponse,
    Locations,
    Experts,
    Specialities,
    Languages,
    Services,
    Premise,
    Building,
    Expert,
    Speciality,
    Language,
    CampaignService,
    Models
} from "../types";

import {
    expertStore,
    premiseStore,
    buildingStore,
    languageStore,
    specialityStore
} from "../store/stores";

import { timanttiApiUrl as apiUrl } from "../settings";


// uuid cache (currently only used by getByUuid function):

const createCache = <T>() => {
    const cache = new Map<string, { time: number; response: T }>();

    const get = (key: string) => {
        return cache.get(key);
    };

    const add = (key: string, response: T) => {
        const responseObj = {
            time: new Date().getTime(),
            response
        };

        cache.set(key, responseObj);
    };

    return {
        get,
        add
    };
};

const entityCache = createCache();


// API fetch functions:

const getLocations = async (
    filters: string[],
    position?: GeolocationPosition | null
): Promise<DataResponse<Locations>> => {
    const locationData = position && {
        lon: position.coords.longitude.toString(),
        lat: position.coords.latitude.toString(),
    };

    const response = await fetch(
        `${apiUrl}/locations?` + new URLSearchParams({
            filter: filters.join(","),
            ...locationData,
        })
    );

    return await response.json();
};


const getExperts = async (
    filters: string[]
): Promise<DataResponse<Experts>> => {
    const response = await fetch(
        `${apiUrl}/experts?` + new URLSearchParams({
            filter: filters.join(",")
        })
    );

    return await response.json();
};


const getSpecialities = async (
    filters: string[]
): Promise<DataResponse<Specialities>> => {
    const response = await fetch(
        `${apiUrl}/specialities?` + new URLSearchParams({
            filter: filters.join(",")
        })
    );

    return await response.json();
};


const getLanguages = async (
    filters: string[]
): Promise<DataResponse<Languages>> => {
    const response = await fetch(
        `${apiUrl}/languages?` + new URLSearchParams({
            filter: filters.join(",")
        })
    );

    return await response.json();
};


const getServices = async (
    filters: string[],
): Promise<DataResponse<Services>> => {
    const response = await fetch(
        `${apiUrl}/services?` + new URLSearchParams({
            filter: filters.join(",")
        })
    );

    return await response.json();
};


const getAllServices = async (): Promise<DataResponse<CampaignService[]>> => {
    const response = await fetch(`${apiUrl}/services/tree`);

    return await response.json();
};


// Premise in the frontend means location/toimipaikka in the backend.
// ToDo: Refactor to match backend naming.
const getPremise = async (uuid: string): Promise<EntityResponse<Premise>> => {
    const cached = await premiseStore.getValueByUuid(uuid);

    if (cached) return cached as EntityResponse<Premise>;

    const response = await fetch(`${apiUrl}/locations/${uuid}`);

    const data: EntityResponse<Premise> = await response.json();

    premiseStore.updateState(premiseStore.subject, data);

    return data;
};


// Building in the frontend means premise/toimitila in the backend.
// ToDo: Refactor to match backend naming.
const getBuilding = async (uuid: string): Promise<EntityResponse<Building>> => {
    const cached = await buildingStore.getValueByUuid(uuid);

    if (cached) return cached as EntityResponse<Building>;

    const response = await fetch(`${apiUrl}/premises/${uuid}`);

    const data: EntityResponse<Building> = await response.json();

    buildingStore.updateState(buildingStore.subject, data);

    return data;
};


const getExpert = async (uuid: string): Promise<EntityResponse<Expert>> => {
    const cached = await expertStore.getValueByUuid(uuid);

    if (cached) return cached as EntityResponse<Expert>;

    const response = await fetch(`${apiUrl}/experts/${uuid}`);

    const data: EntityResponse<Expert> = await response.json();

    expertStore.updateState(expertStore.subject, data);

    return data;
};


const getSpeciality = async (uuid: string): Promise<EntityResponse<Speciality>> => {
    const cached = await specialityStore.getValueByUuid(uuid);

    if (cached) return cached as EntityResponse<Speciality>;

    const response = await fetch(`${apiUrl}/specialities/${uuid}`);

    const data: EntityResponse<Speciality> = await response.json();

    specialityStore.updateState(specialityStore.subject, data);

    return data;
};


const getLanguage = async (uuid: string): Promise<EntityResponse<Language>> => {
    const cached = await languageStore.getValueByUuid(uuid);

    if (cached) return cached as EntityResponse<Language>;

    const response = await fetch(`${apiUrl}/languages/${uuid}`);

    const data: EntityResponse<Language> = await response.json();

    languageStore.updateState(languageStore.subject, data);

    return data;
};

interface MapBoxTokenContainer {
    expires: string,
    token: string
}

let mapBoxToken: MapBoxTokenContainer|null = null;

const getMapBoxToken = async (): Promise<MapBoxTokenContainer|null> => {
    if (mapBoxToken !== null && moment(mapBoxToken.expires) > moment(new Date()))
        return mapBoxToken;

    const response = await fetch(`${apiUrl}/misc/mapboxtoken`);

    const responseData = await response.json() as MapBoxTokenResponse;

    if (responseData.success) {
        mapBoxToken = responseData.data;
        return mapBoxToken;
    }
    else throw new Error("Couldn't get token");
};

const getByUuid = async (uuid: string):
Promise<EntityResponse<Expert|Premise|Language|Speciality>> => {
    const cached: any = await entityCache.get(uuid);

    if (cached) return cached.response;

    const response = await fetch(`${apiUrl}/fetch/${uuid}`);

    const data = await response.json();

    entityCache.add(uuid, data);

    return data;
};


type ICreatePopulateUuids =
    EntityResponse<Expert | Premise | Language | Speciality> | CampaignService | null

const populateFilters = async (
    uuid: string, services: CampaignService[]
): Promise<ICreatePopulateUuids> => {
    const service = services.find(service => service.uuid === uuid);
    if (service) return service;

    let populated = null;

    try {
        populated = await getByUuid(uuid);
    } catch (e) {
        populated = null;
        console.error("Error when fetching with uuid from API.", e);
    }

    if (!populated) return null;

    const expert = populated.model === Models.työntekijä ?
        populated as EntityResponse<Expert> :
        null;
    const premise = populated.model === Models.toimipaikka ?
        populated as EntityResponse<Premise> :
        null;
    const language = populated.model === Models.kieli ?
        populated as EntityResponse<Language> :
        null;
    const speciality = populated.model === Models.erityisosaaminen ?
        populated as EntityResponse<Speciality> :
        null;

    return expert || premise || language || speciality || null;
};


export default {
    getAllServices,
    getLocations,
    getExperts,
    getSpecialities,
    getLanguages,
    getServices,
    getPremise,
    getExpert,
    getSpeciality,
    getLanguage,
    getBuilding,
    getMapBoxToken,
    populateFilters
};
